import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { Block } from "@/types/proposals";

interface PackageLineItemsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  block: Block;
  onUpdate: (updates: Partial<Block>) => void;
}

export function PackageLineItemsDialog({ 
  open, 
  onOpenChange, 
  block, 
  onUpdate 
}: PackageLineItemsDialogProps) {
  const [items, setItems] = useState<any[]>([]);
  const [hiddenIds, setHiddenIds] = useState<string[]>(
    block.settings?.visibilitySettings?.hiddenLineItemIds || []
  );

  // Load package items (mock data for preview)
  useEffect(() => {
    // In real implementation, fetch from packageGroupIds
    setItems([
      { id: '1', name: 'Shingles - Architectural', quantity: 45, unit_price: 120 },
      { id: '2', name: 'Underlayment - Synthetic', quantity: 12, unit_price: 85 },
      { id: '3', name: 'Ridge Cap', quantity: 8, unit_price: 45 },
      { id: '4', name: 'Ice & Water Shield', quantity: 6, unit_price: 95 },
      { id: '5', name: 'Drip Edge', quantity: 120, unit_price: 3.5 },
    ]);
  }, [block]);

  const toggleItem = (itemId: string) => {
    setHiddenIds(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSave = () => {
    onUpdate({
      settings: {
        ...block.settings,
        visibilitySettings: {
          ...block.settings?.visibilitySettings,
          hiddenLineItemIds: hiddenIds
        }
      }
    });
    onOpenChange(false);
  };

  const visibleItems = items.filter(item => !hiddenIds.includes(item.id));
  const totalPrice = 12450; // Mock locked price

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Configure Package Line Items</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-4">
          {/* Left: Settings */}
          <div>
            <h4 className="font-semibold mb-3">Select Visible Items</h4>
            <p className="text-xs text-muted-foreground mb-3">
              Unchecked items will be hidden from customer view
            </p>
            <ScrollArea className="h-[400px] border rounded p-3">
              {items.map(item => (
                <div key={item.id} className="flex items-start gap-3 mb-3 p-2 hover:bg-muted rounded">
                  <Checkbox
                    checked={!hiddenIds.includes(item.id)}
                    onCheckedChange={() => toggleItem(item.id)}
                  />
                  <div className="flex-1">
                    <Label className="font-medium cursor-pointer">
                      {item.name}
                    </Label>
                    <div className="text-xs text-muted-foreground mt-1">
                      Qty: {item.quantity} × ${item.unit_price} = ${item.quantity * item.unit_price}
                    </div>
                  </div>
                </div>
              ))}
            </ScrollArea>
          </div>

          {/* Right: Live Preview */}
          <div>
            <h4 className="font-semibold mb-3">Customer View Preview</h4>
            <Card className="p-4 bg-muted border-2 h-[400px] overflow-y-auto">
              <div className="space-y-2">
                {visibleItems.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Only total will be shown
                  </p>
                ) : (
                  visibleItems.map(item => (
                    <div key={item.id} className="text-sm py-1 border-b">
                      • {item.name}
                    </div>
                  ))
                )}
                <Separator className="my-4" />
                <div className="text-2xl font-bold text-primary">
                  Total: ${totalPrice.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground italic">
                  * Price locked from job estimate
                </p>
              </div>
            </Card>
          </div>
        </div>

        <div className="flex justify-between items-center pt-4">
          <p className="text-xs text-muted-foreground">
            {hiddenIds.length} items hidden • Total price remains ${totalPrice.toLocaleString()}
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
